import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import { format } from "date-fns";

const REASONS = ["too long", "wrong tone", "off topic", "inaccurate", "duplicate", "low quality"];
const errMsg = (e) => e?.response?.data?.message || e.message || "Something went wrong";
const scoreChip = (s) => (s >= 80 ? "green" : s >= 60 ? "amber" : "red");

const FlowApprovals = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [edits, setEdits] = useState({});
  const [rejecting, setRejecting] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await axiosInstance.get("flows/approvals"); setItems(data.data || []); }
    catch (e) { swal("Error", errMsg(e), "error"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChanged && onChanged(); };

  const act = async (it, fn, okMsg) => {
    setBusy(it._id);
    try { const r = await fn(); if (okMsg) swal(okMsg, r?.data?.message || "", "success"); done(); }
    catch (e) { swal("Could not do that", errMsg(e), "error"); }
    setBusy("");
  };
  const approve = (it) => act(it, () => axiosInstance.post(`flows/approvals/${it._id}/approve`, edits[it._id] !== undefined ? { text: edits[it._id] } : {}));
  const reject = (it, reason) => { setRejecting(""); act(it, () => axiosInstance.post(`flows/approvals/${it._id}/reject`, { reason })); };
  const regenerate = (it) => act(it, async () => { const r = await axiosInstance.post(`flows/approvals/${it._id}/regenerate`); setEdits((x) => { const n = { ...x }; delete n[it._id]; return n; }); return r; });
  const approveAll = async () => {
    try { const { data } = await axiosInstance.post("flows/approvals/approve-all", { minScore: 80 }); swal("Done", data.message, "success"); done(); }
    catch (e) { swal("Error", errMsg(e), "error"); }
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="success" /></div>;
  if (!items.length) return (
    <div className="rf-card rf-empty"><div className="ico">🛡️</div><h5>Nothing waiting for approval</h5><p className="mb-0">When a flow writes a post that needs your OK, it shows up here.</p></div>
  );

  return (
    <>
      <div className="d-flex align-items-center mb-3">
        <h6 className="mb-0">{items.length} waiting</h6><div className="rf-spacer" />
        <button className="btn-rf-o" onClick={approveAll}>Approve all with score 80+</button>
      </div>
      {items.map((it) => {
        const isEdit = edits[it._id] !== undefined;
        return (
          <div className="rf-card" key={it._id}>
            <div className="d-flex flex-wrap flex-md-nowrap" style={{ gap: 14 }}>
              {it.imageUrl && <img src={it.imageUrl} alt="" referrerPolicy="no-referrer" style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 10 }} onError={(e) => (e.target.style.display = "none")} />}
              <div className="flex-fill">
                <div className="mb-2">
                  <span className={`rf-chip ${scoreChip(it.score)}`}>Score {it.score}</span>{" "}
                  <span className="rf-meta">{it.flowName} → {it.channelName}{it.scheduledFor ? ` · goes out ${format(new Date(it.scheduledFor), "dd MMM HH:mm")}` : ""}</span>
                </div>
                {isEdit
                  ? <textarea rows={6} className="form-control" value={edits[it._id]} onChange={(e) => setEdits({ ...edits, [it._id]: e.target.value })} />
                  : <div style={{ whiteSpace: "pre-wrap" }}>{it.text}</div>}
                {it.issues?.length > 0 && <div className="rf-help" style={{ color: "#9a6b00" }}>AI notes: {it.issues.join("; ")}</div>}
              </div>
            </div>
            <div className="d-flex flex-wrap mt-3" style={{ gap: 8 }}>
              <button className="btn-rf" disabled={busy === it._id} onClick={() => approve(it)}>{busy === it._id ? <Spinner animation="border" size="sm" /> : isEdit ? "Save & approve" : "Approve"}</button>
              {isEdit
                ? <button className="btn-rf-o" onClick={() => setEdits((x) => { const n = { ...x }; delete n[it._id]; return n; })}>Cancel edit</button>
                : <button className="btn-rf-o" onClick={() => setEdits({ ...edits, [it._id]: it.text })}>Edit</button>}
              <button className="btn-rf-o" disabled={busy === it._id} onClick={() => regenerate(it)}>Regenerate</button>
              <div style={{ position: "relative" }}>
                <button className="btn-rf-danger" onClick={() => setRejecting(rejecting === it._id ? "" : it._id)}>Reject ▾</button>
                {rejecting === it._id && (
                  <div className="dropdown-menu show" style={{ position: "absolute" }}>
                    {REASONS.map((r) => <button key={r} className="dropdown-item" onClick={() => reject(it, r)}>{r}</button>)}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default FlowApprovals;
